import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { approveAppointment, rejectAppointment } from '../../api/appointments';
import { confirmOrder, rejectOrder } from '../../api/orders';
import { useToast } from '../../context/ToastContext';

const formatLkr = (n) => `LKR ${Number(n || 0).toLocaleString()}`;

const formatDate = (d, locale) =>
  d ? new Date(d).toLocaleDateString(locale, { month: 'short', day: 'numeric' }) : '—';

const PendingApprovalsPanel = ({ appointments = [], orders = [] }) => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['salon-dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['orders'] });
    queryClient.invalidateQueries({ queryKey: ['appointments'] });
    queryClient.invalidateQueries({ queryKey: ['order-summary'] });
  };

  const approveApptMut = useMutation({
    mutationFn: approveAppointment,
    onSuccess: () => {
      showToast(t('toast.bookingApproved'), 'success');
      invalidate();
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.approveFailed'), 'error'),
  });

  const rejectApptMut = useMutation({
    mutationFn: ({ id, reason }) => rejectAppointment(id, reason),
    onSuccess: () => {
      showToast(t('toast.bookingRejected'), 'success');
      setRejectingId(null);
      setRejectReason('');
      invalidate();
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.rejectFailed'), 'error'),
  });

  const confirmOrderMut = useMutation({
    mutationFn: confirmOrder,
    onSuccess: () => {
      showToast(t('toast.orderConfirmed'), 'success');
      invalidate();
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.confirmFailed'), 'error'),
  });

  const rejectOrderMut = useMutation({
    mutationFn: ({ id, reason }) => rejectOrder(id, reason),
    onSuccess: () => {
      showToast(t('toast.orderRejected'), 'success');
      setRejectingId(null);
      setRejectReason('');
      invalidate();
    },
    onError: (err) => showToast(err.response?.data?.message || t('toast.rejectFailed'), 'error'),
  });

  const busy =
    approveApptMut.isPending ||
    rejectApptMut.isPending ||
    confirmOrderMut.isPending ||
    rejectOrderMut.isPending;

  const startReject = (key) => {
    setRejectingId(key);
    setRejectReason('');
  };

  const submitReject = (type, id) => {
    const reason = rejectReason.trim();
    if (!reason) {
      showToast(t('toast.rejectionReasonRequired'), 'error');
      return;
    }
    if (type === 'appointment') {
      rejectApptMut.mutate({ id, reason });
    } else {
      rejectOrderMut.mutate({ id, reason });
    }
  };

  const total = appointments.length + orders.length;
  if (total === 0) return null;

  return (
    <section className="mb-8 rounded-xl border border-amber-800/50 bg-amber-950/15 p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-medium text-amber-100">{t('dashboard.pendingApprovals.title')}</h3>
          <p className="text-xs text-amber-200/70">
            {t('dashboard.pendingApprovals.subtitle')}
          </p>
        </div>
        <span className="rounded-full bg-amber-900/50 px-3 py-1 text-xs font-medium text-amber-200">
          {t('dashboard.pendingApprovals.waiting', { count: total })}
        </span>
      </div>

      <div className="space-y-3">
        {appointments.map((a) => {
          const rejectKey = `appt-${a._id}`;
          const isRejecting = rejectingId === rejectKey;
          return (
            <div key={a._id} className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/80 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-glow-600/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-glow-300">
                      {t('dashboard.pendingApprovals.service')}
                    </span>
                    <span className="text-xs text-zinc-600 dark:text-zinc-500">{formatDate(a.createdAt, i18n.language)}</span>
                  </div>
                  <p className="mt-1 font-medium">{a.serviceName}</p>
                  <p className="text-sm text-zinc-600 dark:text-zinc-500">
                    {a.clientName} · {a.branchName}
                  </p>
                  <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                    {t('dashboard.pendingApprovals.atTime', {
                      date: formatDate(a.preferredDate, i18n.language),
                      time: a.preferredTime,
                    })}{' '}
                    · {formatLkr(a.price)}
                  </p>
                </div>
                {!isRejecting && (
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => approveApptMut.mutate(a._id)}
                      className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
                    >
                      {t('admin.tenants.approve')}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => startReject(rejectKey)}
                      className="rounded-lg border border-red-800/60 px-3 py-1.5 text-xs text-red-300 hover:bg-red-950/40 disabled:opacity-50"
                    >
                      {t('admin.tenants.reject')}
                    </button>
                  </div>
                )}
              </div>
              {isRejecting && (
                <div className="mt-3 space-y-2 border-t border-zinc-200 dark:border-zinc-800 pt-3">
                  <input
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder={t('dashboard.pendingApprovals.rejectReasonPlaceholder')}
                    className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => submitReject('appointment', a._id)}
                      className="rounded-lg bg-red-600/90 px-3 py-1.5 text-xs text-white disabled:opacity-50"
                    >
                      {t('dashboard.pendingApprovals.confirmReject')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setRejectingId(null)}
                      className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 text-xs text-zinc-600 dark:text-zinc-400"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {orders.map((o) => {
          const rejectKey = `order-${o._id}`;
          const isRejecting = rejectingId === rejectKey;
          return (
            <div key={o._id} className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/80 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-blue-500/20 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-blue-300">
                      {t('dashboard.pendingApprovals.order')}
                    </span>
                    <span className="text-xs text-zinc-600 dark:text-zinc-500">{formatDate(o.createdAt, i18n.language)}</span>
                  </div>
                  <p className="mt-1 font-medium">
                    {t('orders.drawer.orderNumber', { number: o._id.slice(-8).toUpperCase() })}
                  </p>
                  <p className="text-sm text-zinc-600 dark:text-zinc-500">
                    {o.clientName} · {o.branchName || t('dashboard.pendingApprovals.pickup')}
                  </p>
                  <p className="mt-1 text-xs text-zinc-600 dark:text-zinc-400">
                    {t('common.itemCount', { count: o.itemCount })} · {formatLkr(o.totalAmount)}
                  </p>
                </div>
                {!isRejecting && (
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => confirmOrderMut.mutate(o._id)}
                      className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
                    >
                      {t('admin.tenants.approve')}
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => startReject(rejectKey)}
                      className="rounded-lg border border-red-800/60 px-3 py-1.5 text-xs text-red-300 hover:bg-red-950/40 disabled:opacity-50"
                    >
                      {t('admin.tenants.reject')}
                    </button>
                  </div>
                )}
              </div>
              {isRejecting && (
                <div className="mt-3 space-y-2 border-t border-zinc-200 dark:border-zinc-800 pt-3">
                  <input
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    placeholder={t('dashboard.pendingApprovals.rejectReasonPlaceholder')}
                    className="w-full rounded-lg border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-3 py-2 text-sm"
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => submitReject('order', o._id)}
                      className="rounded-lg bg-red-600/90 px-3 py-1.5 text-xs text-white disabled:opacity-50"
                    >
                      {t('dashboard.pendingApprovals.confirmReject')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setRejectingId(null)}
                      className="rounded-lg border border-zinc-300 dark:border-zinc-700 px-3 py-1.5 text-xs text-zinc-600 dark:text-zinc-400"
                    >
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PendingApprovalsPanel;
